import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Send, Tag, FileText, BookOpen, Pill, HelpCircle, MessageCircle, Flame, Brain, ShieldAlert, Plus } from 'lucide-react';
import api from '../../utils/api';
import Navbar from '../../components/marketing/Navbar';
import Footer from '../../components/marketing/Footer';

const CATEGORIES = [
    { label: 'Case Discussion', value: 'Case Discussion', icon: FileText, desc: 'Share a clinical case for discussion', color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
    { label: 'Guidelines', value: 'Guidelines', icon: BookOpen, desc: 'Discuss clinical guidelines', color: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
    { label: 'Drug Info', value: 'Drug Info', icon: Pill, desc: 'Drug & pharmacotherapy', color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
    { label: 'Question', value: 'Question', icon: HelpCircle, desc: 'Ask a clinical question', color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
    { label: 'General', value: 'General', icon: MessageCircle, desc: 'General discussion', color: 'text-slate-700 dark:text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/30' },
];

const CreatePost = () => {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [category, setCategory] = useState('Case Discussion');
    const [tagsInput, setTagsInput] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const user = (() => {
        try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
    })();

    if (!user) {
        return (
            <div className="dark min-h-screen bg-[#0B1120] text-slate-200 flex flex-col items-center justify-center p-4">
                <ShieldAlert size={64} className="text-slate-700 mb-6" />
                <h2 className="text-2xl font-bold mb-4 text-slate-900 dark:text-white">Login Required</h2>
                <p className="text-slate-600 dark:text-slate-400 mb-8 max-w-md text-center">You must be logged in to start a new clinical discussion.</p>
                <Link to="/login" className="px-8 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-slate-900 dark:text-white rounded-xl font-bold transition-all shadow-lg shadow-emerald-500/20">
                    Log In to Continue
                </Link>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim() || !body.trim()) {
            setError('Please provide both a title and details for your discussion.');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
            const res = await api.post('/community/posts', { title, body, category, tags });
            navigate(`/community/${res.data._id}`);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create discussion. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="dark min-h-screen bg-[#0B1120] text-slate-200 selection:bg-emerald-500/30 flex flex-col font-sans">
            <Navbar />

            {/* Ambient Background Glow */}
            <div className="fixed top-0 inset-x-0 h-[500px] bg-gradient-to-b from-blue-900/10 via-emerald-900/5 to-transparent pointer-events-none -z-10" />

            {/* Main Container - Adjusted padding for fixed Navbar */}
            <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10 pt-24 md:pt-36 w-full flex flex-col">

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 pb-4 md:pb-6 border-b border-white/5 gap-4">
                    <div>
                        <Link to="/community" className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-3 md:mb-4 transition-colors font-medium group text-sm w-fit px-3 py-1.5 rounded-lg hover:bg-white/5">
                            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> Back to Feed
                        </Link>
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight flex items-center gap-2 md:gap-3">
                            <div className="p-2 md:p-2.5 bg-white/5 border border-white/10 rounded-xl shadow-inner shrink-0">
                                <Plus size={20} className="text-emerald-400 md:w-6 md:h-6" />
                            </div>
                            Start a Discussion
                        </h1>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-10 pb-8">

                    {/* Main Form Area */}
                    <div className="lg:col-span-2">
                        <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8 bg-[#0F172A]/80 backdrop-blur-xl border border-white/5 rounded-2xl md:rounded-3xl p-5 md:p-8 shadow-2xl relative overflow-hidden">

                            {/* Form Header Glow */}
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-teal-500 opacity-50" />

                            {/* Title */}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Discussion Title <span className="text-red-400">*</span></label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="e.g., 65yo M with VRE endocarditis"
                                    maxLength={200}
                                    className="w-full px-4 md:px-5 py-3 md:py-4 bg-slate-50/50 dark:bg-slate-900/50 border border-white/10 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 md:focus:ring-4 md:focus:ring-emerald-500/10 text-base sm:text-lg md:text-xl font-semibold transition-all"
                                />
                                <div className="flex justify-between items-center mt-2">
                                    <p className="text-[10px] sm:text-xs text-slate-600 dark:text-slate-400 font-medium tracking-wide">Be specific. Imagine you're asking a colleague.</p>
                                    <p className="text-[10px] sm:text-xs text-slate-600 dark:text-slate-400 font-mono">{title.length}/200</p>
                                </div>
                            </div>

                            {/* Body Editor */}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2 flex items-center justify-between">
                                    <span>Clinical Details <span className="text-red-400">*</span></span>
                                    <span className="text-xs text-emerald-400 font-medium bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">Markdown Supported</span>
                                </label>
                                <div className="relative group">
                                    <textarea
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        placeholder={`Share the details of your case or question...

Pro-tips for better engagement:
- Include relevant PMH, labs, and vitals.
- State your specific question clearly.
- Use Markdown: **Bold**, *Italic*, or \`code\` for lab values.
- Please DO NOT include any Protected Health Information (PHI).`}
                                        rows={14}
                                        maxLength={10000}
                                        className="w-full lg:min-h-[400px] px-5 py-4 bg-black/20 border border-white/10 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500/80 outline-none focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/10 resize-y font-mono text-sm leading-relaxed transition-all"
                                    />

                                    {/* Markdown Hint Footer */}
                                    <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center pointer-events-none opacity-50 group-focus-within:opacity-100 transition-opacity">
                                        <div className="flex gap-4 text-xs font-semibold text-slate-700 dark:text-slate-400">
                                            <span>**Bold**</span>
                                            <span>*Italic*</span>
                                            <span>### Header</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center mt-2">
                                    <p className="text-xs text-amber-500/80 font-medium">⚠️ Never post recognizable patient information.</p>
                                    <p className="text-xs text-slate-600 dark:text-slate-400 font-mono">{body.length}/10000</p>
                                </div>
                            </div>

                            {/* Tags Input */}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Tags <span className="text-slate-600 dark:text-slate-400 font-normal">(Optional)</span></label>
                                <div className="relative focus-within:z-10 group">
                                    <Tag size={16} className="absolute left-3.5 md:left-4 top-1/2 -translate-y-1/2 text-slate-600 dark:text-slate-400 group-focus-within:text-emerald-400 transition-colors md:w-[18px] md:h-[18px]" />
                                    <input
                                        type="text"
                                        value={tagsInput}
                                        onChange={(e) => setTagsInput(e.target.value)}
                                        placeholder="e.g. ID, vanc, ICU (comma separated)"
                                        className="w-full pl-10 md:pl-12 pr-4 md:pr-5 py-3 md:py-4 bg-slate-50/50 dark:bg-slate-900/50 border border-white/10 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 md:focus:ring-4 md:focus:ring-emerald-500/10 font-medium transition-all text-sm md:text-base"
                                    />
                                </div>
                            </div>

                            {/* Error Alert */}
                            {error && (
                                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                                    <ShieldAlert className="text-red-400 shrink-0 mt-0.5" size={18} />
                                    <p className="text-red-300 text-sm font-medium">{error}</p>
                                </div>
                            )}

                            {/* Submit Actions */}
                            <div className="flex items-center justify-end gap-3 md:gap-4 pt-6 border-t border-white/5">
                                <Link to="/community" className="px-4 md:px-6 py-2.5 md:py-3.5 text-sm md:text-base text-slate-700 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-medium transition-colors hover:bg-white/5 rounded-xl">
                                    Cancel
                                </Link>
                                <button
                                    type="submit"
                                    disabled={submitting || !title.trim() || !body.trim()}
                                    className="flex items-center gap-2 px-6 md:px-8 py-2.5 md:py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:from-slate-800 disabled:to-slate-800 disabled:text-slate-600 dark:text-slate-400 disabled:cursor-not-allowed text-slate-900 dark:text-white rounded-xl font-bold transition-all shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:shadow-emerald-500/30 hover:-translate-y-0.5 text-sm md:text-base whitespace-nowrap"
                                >
                                    {submitting ? (
                                        <>
                                            <div className="w-4 h-4 md:w-5 md:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin shrink-0" />
                                            Publishing...
                                        </>
                                    ) : (
                                        <>
                                            <Send size={16} className="md:w-[18px] md:h-[18px] shrink-0" /> Publish
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Right Sidebar - Selection & Tips */}
                    <div className="lg:col-span-1 space-y-6">

                        {/* Category Selector Block */}
                        <div className="bg-[#0F172A]/80 backdrop-blur-xl border border-white/5 rounded-3xl p-5 md:p-6 shadow-xl sticky top-24 z-10 w-full overflow-hidden">
                            <h3 className="text-xs md:text-sm font-bold text-slate-700 dark:text-slate-300 mb-3 md:mb-4 uppercase tracking-widest hidden lg:block">Select Topic</h3>

                            {/* Mobile Horizontal scroll / Desktop Vertical stack */}
                            <div className="flex lg:flex-col overflow-x-auto lg:overflow-visible gap-2 md:gap-3 pb-2 lg:pb-0 hide-scrollbar -mx-2 px-2 lg:mx-0 lg:px-0">
                                {CATEGORIES.map(cat => (
                                    <button
                                        key={cat.value}
                                        type="button"
                                        onClick={() => setCategory(cat.value)}
                                        className={`shrink-0 lg:w-full flex items-center lg:items-start gap-2 md:gap-3 p-2.5 md:p-3.5 rounded-2xl border text-left transition-all ${category === cat.value
                                            ? `${cat.bg} ${cat.border} ring-1 ring-inset ring-${cat.color.split('-')[1]}-500/50 shadow-sm`
                                            : 'bg-white/[0.02] border-white/5 text-slate-700 dark:text-slate-400 hover:bg-white/[0.04] hover:border-white/10'
                                            }`}
                                    >
                                        <cat.icon size={18} className={`md:w-5 md:h-5 lg:mt-0.5 shrink-0 ${category === cat.value ? cat.color : 'text-slate-700 dark:text-slate-400'}`} />
                                        <div>
                                            <div className={`text-xs md:text-sm font-bold whitespace-nowrap lg:whitespace-normal ${category === cat.value ? cat.color : 'text-slate-700 dark:text-slate-300'}`}>
                                                {cat.label}
                                            </div>
                                            <div className="hidden lg:block text-xs opacity-70 mt-1 leading-relaxed md:leading-relaxed">
                                                {cat.desc}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {/* Cortex AI Integration Notice */}
                            <div className="mt-4 md:mt-8 p-4 md:p-5 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border border-emerald-500/20 rounded-2xl relative overflow-hidden group">
                                <div className="absolute -right-4 -top-4 w-20 h-20 md:w-24 md:h-24 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/20 transition-all duration-700" />
                                <div className="flex items-center gap-2 md:gap-3 mb-2">
                                    <div className="p-1 md:p-1.5 bg-emerald-500/20 rounded-lg">
                                        <Brain className="text-emerald-400 w-4 h-4 md:w-4 md:h-4" />
                                    </div>
                                    <h4 className="text-xs md:text-sm font-bold text-emerald-400">Cortex AI Analysis</h4>
                                </div>
                                <p className="text-[10px] md:text-xs text-emerald-100/80 leading-relaxed md:leading-relaxed relative z-10">
                                    Upon publishing, our clinical AI model will instantly analyze your post and provide initial thoughts, differential diagnoses, or drug information to kickstart the community discussion.
                                </p>
                            </div>

                        </div>
                    </div>

                </div>
            </div>
            <Footer />
        </div>
    );
};

export default CreatePost;
